import React from "react";
import {
  BadgePercent,
  Bug,
  Calendar,
  CalendarCheck,
  CalendarCheck2,
  CalendarClock,
  CircleGauge,
  Hash,
  Images,
  ListTodo,
  MessageSquare,
  PhoneCall,
  SquareCheck,
  Star,
  Timer,
  UsersRound,
  Zap,
} from "lucide-react";

import {
  RecordIdIcon,
  NameIcon,
  DescriptionIcon,
  DealNameIcon,
  ContactIcon,
  CompanyIcon,
  AvatarUrlIcon,
  JobTitleIcon,
  AssociatedPeopleIcon,
  AssociatedCompanyIcon,
  DealStageIcon,
  DealValueIcon,
  DealOwnerIcon,
  ListEntriesIcon,
  PrimaryLocationIcon,
  PhoneNumbersIcon,
  EmailAddressesIcon,
  LabelsIcon,
  CategoriesIcon,
  DomainsIcon,
  LinkedinIcon,
  FacebookIcon,
  TwitterIcon,
  InstagramIcon,
} from "../icons/fieldTypeIcons";

import DataFieldsIcon from "../icons/DataFieldsIcon";

// ---- Field ID → Icon (specific field identifiers) ----
type FieldIconEntry = {
  icon: React.ComponentType<{ className?: string }>;
  /** Optional: use a different icon per formType (e.g. "name" differs for SALE/CONTACT/COMPANY/PRODUCT/ACTIVITY) */
  byFormType?: Record<string, React.ComponentType<{ className?: string }>>;
};

export const FIELD_ICON_BY_ID: Record<string, FieldIconEntry> = {
  id: { icon: RecordIdIcon },
  name: {
    icon: NameIcon,
    byFormType: {
      SALE: DealNameIcon,
      CONTACT: ContactIcon,
      COMPANY: CompanyIcon,
      PRODUCT: NameIcon,
      ACTIVITY: NameIcon,
    },
  },
  labels: { icon: LabelsIcon },
  status: { icon: DealStageIcon },
  source: { icon: CategoriesIcon },
  stage: { icon: DealStageIcon },
  bucketId: { icon: CircleGauge },
  estimatedRevenue: { icon: DealValueIcon },
  price: { icon: DealValueIcon },
  unitPrice: { icon: DealValueIcon },
  estimatedProbability: { icon: BadgePercent },
  tax: { icon: BadgePercent },
  connectionStrength: { icon: Zap },
  assignees: { icon: DealOwnerIcon },
  start: { icon: Calendar },
  finish: { icon: CalendarCheck2 },
  nextActivityDatetime: { icon: CalendarCheck },
  description: { icon: DescriptionIcon },
  email: { icon: EmailAddressesIcon },
  phone: { icon: PhoneNumbersIcon },
  domains: { icon: DomainsIcon },
  website: { icon: DomainsIcon },
  country: { icon: PrimaryLocationIcon },
  industry: { icon: CategoriesIcon },
  productCode: { icon: NameIcon },
  imageIds: { icon: Images },
  avatarUrl: { icon: AvatarUrlIcon },
  jobTitle: { icon: JobTitleIcon },

  // Interaction / Activity fields
  firstComment: { icon: MessageSquare },
  lastComment: { icon: MessageSquare },
  firstEmail: { icon: EmailAddressesIcon },
  lastEmail: { icon: EmailAddressesIcon },
  firstActivity: { icon: Zap },
  lastActivity: { icon: Zap },
  firstMeeting: { icon: UsersRound },
  lastMeeting: { icon: UsersRound },
  firstMeetingInPerson: { icon: UsersRound },
  lastMeetingInPerson: { icon: UsersRound },
  firstCall: { icon: PhoneCall },
  lastCall: { icon: PhoneCall },
  firstTask: { icon: ListTodo },
  lastTask: { icon: ListTodo },
  firstDeadline: { icon: Timer },
  lastDeadline: { icon: Timer },

  // Social / Link fields
  linkedin: { icon: LinkedinIcon },
  angleList: { icon: DomainsIcon },
  facebook: { icon: FacebookIcon },
  twitter: { icon: TwitterIcon },
  instagram: { icon: InstagramIcon },

  // Name subfields
  firstName: { icon: ContactIcon },
  lastName: { icon: ContactIcon },
};

// ---- Field Type → Icon (fallback by data type) ----
export const FIELD_ICON_BY_TYPE: Record<string, React.ComponentType<{ className?: string }>> = {
  // Numeric types
  integer: Hash,
  double: Hash,
  duration: Hash,

  // Text types
  string: DescriptionIcon,
  text: DescriptionIcon,

  // Entity / Relation types
  contact: AssociatedPeopleIcon,
  account: AssociatedCompanyIcon,
  issue: Bug,
  record: ListEntriesIcon,
  relationship: AssociatedPeopleIcon,

  // Collection types
  stringArray: LabelsIcon,

  // Percentage
  percent: BadgePercent,

  // Date/Time
  datetime: Calendar,
  timestamp: CalendarClock,

  // Structured types
  checkbox: SquareCheck,
  rating: Star,
  select: CategoriesIcon,
  multiSelect: LabelsIcon,
  currency: DealValueIcon,
  location: PrimaryLocationIcon,
  phoneNumber: PhoneNumbersIcon,
  user: DealOwnerIcon,
};

// ---- Default fallback icon ----
export const DEFAULT_FIELD_ICON: React.ComponentType<{ className?: string }> = DataFieldsIcon;

/**
 * Resolve the icon component for a given field.
 * Priority: field.id match (with formType variant) → field.type match → default.
 */
export const getFieldIcon = (
  field: { id?: string; type?: string; formType?: string },
  className = "w-4 h-4 text-gray-600"
): React.ReactNode => {
  const fieldId = field.id || "";
  const fieldType = field.type || "";
  const formType = field.formType || "";

  // 1. Try specific field ID match
  const byId = FIELD_ICON_BY_ID[fieldId];
  if (byId) {
    const IconComponent = (formType && byId.byFormType?.[formType]) || byId.icon;
    return <IconComponent className={className} />;
  }

  // 2. Try field type match
  const byType = FIELD_ICON_BY_TYPE[fieldType];
  if (byType) {
    const IconComponent = byType;
    return <IconComponent className={className} />;
  }

  // 3. Fallback
  const FallbackIcon = DEFAULT_FIELD_ICON;
  return <FallbackIcon className={className} />;
};
